import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import Data from 'containers/Data';
import GeneralContentContainer from 'containers/GeneralContentContainer';
import GeneralContentContainerApiComponents from 'containers/GeneralContentContainerApiComponents';
import AbsoluteRedirect from 'components/AbsoluteRedirect';
import SocialPlusForum from 'components/SocialPlusForum';

import ActionHours from '../pages/ActionHours';
import Calendar from '../pages/Calendar';
import CommunityGuidelines from '../pages/CommunityGuidelines';
import CurrentWeek from '../pages/CurrentWeek';
import Dashboard from '../pages/Dashboard';
import FavoritesPage from '../pages/FavoritesPage';
import RecipesPage from '../pages/RecipesPage';
import SearchPage from '../pages/SearchPage';
import TrackDetail from '../pages/TrackDetail';
import WeeksPage from '../pages/WeeksPage';
import ListPage from '../../PBCC/pages/ListPage';
import Onboarding from './Onboarding';

import { useWLC } from '../contexts/WLCContext';
import { getStoredAuthToken } from '../utils';
import {
  WEEKS_LIST_SLUG,
  ONBOARD_CONTENT_ID,
  ACTION_HOURS_ID,
  DEFAULT_COMMUNITY_IDS,
  LIVE_CALLS_SLUG,
  COMMUNITY_GUIDELINES_SLUG,
  EVENT_SLUG
} from '../constants';

/**
 * WLCRoutes – All route definitions for the WLC product.
 *
 * Consumes WLCContext so individual routes no longer need props drilled
 * from the top-level component.
 *
 * @param {string} productSlug – The product path segment (e.g. 'wlc')
 */
const WLCRoutes = ({ productSlug }) => {
  const { isOnboard, toursCompleted, toursLoaded, updateTourStatus } = useWLC();
  const basePath = `/products/${productSlug}`;

  return (
    <Switch>
      {/* Favorites Library */}
      <Route exact path={`${basePath}/favorites-library`} render={props => <FavoritesPage {...props} />} />

      {/* Live Event Replays */}
      <Route
        exact
        path={`${basePath}/live-calls`}
        render={props => (
          <GeneralContentContainer
            overrideSlug={LIVE_CALLS_SLUG}
            Component={ListPage}
            dataMassager={null}
            {...props}
            sortBy="data.time"
          />
        )}
      />

      {/* Search */}
      <Route
        exact
        path={`${basePath}/search`}
        render={() => {
          const token = getStoredAuthToken();
          return <SearchPage authToken={token} />;
        }}
      />

      {/* Absolute redirect for /products */}
      <Route exact path="/products" render={() => <AbsoluteRedirect to="/products/" />} />

      {/* Action Hours */}
      <Route
        exact
        path={`${basePath}/action-hours`}
        render={() => (
          <GeneralContentContainer overrideSlug="action-hours" Component={ActionHours} dataMassager={null} />
        )}
      />

      {/* Dashboard */}
      <Route
        exact
        path={`${basePath}/`}
        render={() => {
          if (!isOnboard) {
            return <Onboarding />;
          } else {
            return <CurrentWeek />;
          }
        }}
      />

      {/* Welcome (onboarding form) */}
      <Route exact path={`${basePath}/welcome`} render={() => <Onboarding />} />

      {/* Resources (same as search) */}
      <Route
        exact
        path={`${basePath}/resources`}
        render={() => {
          const token = getStoredAuthToken();
          return <SearchPage authToken={token} />;
        }}
      />

      {/* Weeks overview */}
      <Route exact path={`${basePath}/weeks`} render={() => <WeeksPage product="wlc" slug={WEEKS_LIST_SLUG} />} />

      {/* Current Week */}
      <Route
        exact
        path={`${basePath}/current-week`}
        render={() => (
          <CurrentWeek toursCompleted={toursCompleted} toursLoaded={toursLoaded} updateTourStatus={updateTourStatus} />
        )}
      />

      {/* Track detail */}
      <Route exact path={`${basePath}/track-detail/:trackSlug`} render={() => <TrackDetail />} />
      <Route exact path={`${basePath}/track-detail`} render={() => <TrackDetail />} />
      <Route exact path={`${basePath}/current-track`} render={() => <TrackDetail />} />

      {/* Recipes */}
      <Route exact path={`${basePath}/recipes`} render={() => <RecipesPage />} />

      {/* Community (forum) */}
      <Route
        exact
        path={`${basePath}/community`}
        render={() => <SocialPlusForum autoJoinCommunityIds={DEFAULT_COMMUNITY_IDS} />}
      />
      <Route
        path={`${basePath}/community/*`}
        render={() => <SocialPlusForum autoJoinCommunityIds={DEFAULT_COMMUNITY_IDS} />}
      />

      {/* Calendar */}
      <Route path={`${basePath}/calendar`} render={() => <Calendar />} />

      {/* Wildcard – dynamic content (week, recipe, action hour, resource) */}
      <Route
        key="wildcard"
        exact
        path="*/:slug/"
        render={() => (
          <GeneralContentContainerApiComponents
            dataMassager={null}
            Component={null}
            componentProps={{
              toursCompleted,
              toursLoaded,
              updateTourStatus
            }}
          />
        )}
      />

      {/* Community guidelines */}
      <Route
        exact
        path={`${basePath}/communityguidelines`}
        component={Data(CommunityGuidelines, `/content/${COMMUNITY_GUIDELINES_SLUG}/WLC`)}
      />

      {/* Catch-all → dashboard */}
      <Redirect from={`${basePath}/*`} to={`${basePath}/`} />
    </Switch>
  );
};

export default WLCRoutes;
